/**
 * Testi italiani della scheda «Artefatti» di un set (ADR 0018 §6): i siti dei progetti,
 * il browser interno e le annotazioni da mandare al relay.
 */

/** Testi degli artifacts. */
export const artifacts = {
  /** Nome accessibile della scheda. */
  title: 'Siti dei progetti del set',
  /** Introduzione. */
  intro: 'Scegli un sito: lo guardi, chiedi le modifiche all’Institore e copi il link da condividere.',
  /** Nessun sito pubblicato. */
  empty: 'Nessun progetto del set ha un sito pubblicato. Quando l’Institore ne pubblica uno, lo trovi qui.',
  /** Lettura dei siti non riuscita. */
  loadFailed: 'Non è stato possibile leggere i siti del set. Riprova.',
  /** Nome accessibile dell'elenco dei siti. */
  listLabel: 'Siti pubblicati',
  /** Chi vede il sito, nell'elenco e in testa alla pagina del sito. */
  audience: {
    private: 'Privata',
    public: 'Visibile a tutti',
    prod: 'Pubblica',
  },
  /** Pagina di un sito: demo o produzione di un progetto. */
  detail: {
    back: 'Tutti i siti',
    title: '{{name}} · {{environment}}',
    loadFailed: 'Non è stato possibile leggere il sito. Riprova.',
    missingTitle: 'Sito non trovato',
    missingText: 'Il progetto non è più nel set, o questo ambiente non c’è.',
    notReady: 'Il sito non è ancora pronto. Ricontrolla fra poco.',
    tabs: {
      site: 'Sito',
      share: 'Condividi',
      info: 'Informazioni',
    },
    site: {
      requestTitle: 'Chiedi una modifica',
      requestHelp: 'Scrivi che cosa cambiare: arriva all’Institore con il progetto e l’ambiente. Per indicare un punto preciso usa «Annota» qui sotto.',
      requestLabel: 'Modifica da chiedere',
      requestPlaceholder: 'Per esempio: nella pagina iniziale il titolo è troppo piccolo',
      requestSend: 'Chiedi all’Institore',
      requestSent: 'Richiesta mandata all’Institore',
      requestFailed: 'Invio non riuscito. Riprova.',
      requestHeading: 'Modifica chiesta dagli Artefatti, {{environment}} ({{url}}):',
      browserTitle: 'Il sito',
      browserHelp: 'Naviga il sito come un visitatore. Con «Annota» prendi una schermata e riquadri le parti da cambiare.',
    },
    share: {
      addressTitle: 'Indirizzo da condividere',
      addressDemoHelp: 'La demo è visibile a tutti: chi ha l’indirizzo la apre.',
      addressProdHelp: 'È il sito pubblico: chi ha l’indirizzo lo apre.',
      linkTitle: 'Link della demo',
      linkHelp: 'La demo è privata: per farla vedere a qualcuno copia un link e mandaglielo.',
      visibilityTitle: 'Chi vede la demo',
      rulesTitle: 'Regole di questo sito',
      rules: {
        demoPrivate1: 'Il link vale finché resti connesso a institorAI. Chi lo riceve non entra nel tuo account.',
        demoPrivate2: '«Revoca i link» chiude tutti i link e le app di demo già installate.',
        demoPublic1: 'Chi conosce l’indirizzo vede la demo senza link, anche dai motori di ricerca.',
        demoPublic2: 'La puoi rendere di nuovo privata quando vuoi. Con il piano Free torna privata da sola.',
        demoCommon: 'La demo ha dati e account suoi, separati dalla produzione. L’Institore lavora qui.',
        prod1: 'La produzione è il sito dei tuoi clienti, con dati e account suoi.',
        prod2: 'Cambia solo con un rilascio, che chiedi all’Institore dopo aver provato la demo.',
      },
    },
    info: {
      manageHelp: 'Domini, spazio, uscita del sito, produzione e test si gestiscono nella pagina «Progetti».',
      manage: 'Gestisci l’hosting',
    },
  },
  /** Nome accessibile del pulsante che apre un sito. */
  openSite: 'Apri {{name}}, {{environment}}',
  /** Browser interno. */
  browser: {
    /** Nome accessibile della barra dell'indirizzo. */
    address: 'Indirizzo della pagina',
    /** Aiuto della barra dell'indirizzo. */
    addressHelp: 'Puoi aprire altre pagine dello stesso sito: scrivi l’indirizzo e premi Invio.',
    /** Indirizzo fuori dal sito. */
    addressOutside: 'Qui si aprono solo pagine di {{origin}}.',
    /** Pulsante di ricarica. */
    reload: 'Ricarica',
    /** Pulsante che apre il sito in una scheda nuova. */
    newTab: 'Apri in una nuova scheda',
    /** Scelta della larghezza della vista. */
    viewport: 'Larghezza della vista',
    /** Larghezze disponibili. */
    viewports: {
      desktop: 'Computer',
      tablet: 'Tablet',
      mobile: 'Telefono',
    },
    /** Titolo dell'iframe, per gli assistivi. */
    frameTitle: 'Sito di {{name}}',
    /** Aiuto per i siti che non si lasciano incorporare. */
    blockedHelp: 'Pagina vuota? Il sito potrebbe non lasciarsi mostrare qui dentro: aprilo in una nuova scheda e carica una schermata per annotarla.',
    /** Pulsante che avvia l'annotazione. */
    annotate: 'Annota',
  },
  /** Cattura della schermata. */
  capture: {
    /** Pulsante che carica una schermata dal computer. */
    upload: 'Carica una schermata',
    /** Aiuto per la cattura. */
    help: 'Il browser ti chiede quale scheda condividere: scegli questa. Si prende un solo fotogramma e la condivisione si chiude subito. In alternativa carica una schermata o incollala qui con Ctrl+V.',
    /** Cattura non disponibile nel browser. */
    unsupported: 'Questo browser non permette di catturare la scheda. Carica una schermata o incollala con Ctrl+V.',
    /** Cattura negata dall'utente. */
    denied: 'Condivisione della scheda non concessa. Riprova o carica una schermata.',
    /** Cattura non riuscita. */
    failed: 'Cattura non riuscita. Riprova o carica una schermata.',
    /** File non immagine. */
    notImage: 'Il file non è un’immagine. Scegli un PNG o un JPEG.',
  },
  /** Annotazione della schermata. */
  annotate: {
    /** Titolo. */
    title: 'Annota la schermata',
    /** Istruzioni. */
    help: 'Trascina sulla schermata per riquadrare una parte, poi scrivi che cosa va cambiato.',
    /** Nome accessibile della schermata. */
    canvas: 'Schermata da annotare: trascina per disegnare un riquadro',
    /** Titolo dell'elenco delle note. */
    notes: 'Note',
    /** Nessun riquadro ancora. */
    noBoxes: 'Ancora nessun riquadro. Trascina sulla schermata per crearne uno.',
    /** Etichetta della nota di un riquadro. */
    noteLabel: 'Nota del riquadro {{number}}',
    /** Segnaposto della nota. */
    notePlaceholder: 'Che cosa non va e che cosa va fatto',
    /** Nota mancante. */
    noteMissing: 'Scrivi che cosa va cambiato in questo riquadro.',
    /** Pulsante che toglie un riquadro. */
    remove: 'Togli il riquadro {{number}}',
    /** Pulsante che toglie l'ultimo riquadro. */
    undo: 'Annulla l’ultimo riquadro',
    /** Pulsante che scarta la schermata. */
    discard: 'Scarta la schermata',
    /** Nota generale facoltativa. */
    general: 'Nota generale (facoltativa)',
    /** Pulsante di invio. */
    send: 'Invia all’Institore',
    /** Invio in corso. */
    sending: 'Invio in corso…',
    /** Invio riuscito. */
    sent: 'Revisione inviata all’Institore.',
    /** Collegamento alla conversazione dopo l'invio. */
    openConversation: 'Apri la conversazione',
    /** Pulsante per annotare un'altra schermata. */
    another: 'Annota un’altra schermata',
    /** Caricamento non riuscito. */
    uploadFailed: 'Caricamento della schermata non riuscito. Riprova.',
    /** Invio non riuscito. */
    sendFailed: 'Invio del messaggio non riuscito. Riprova.',
  },
  /** Testo del messaggio mandato al relay. */
  review: {
    /** Prima riga. */
    heading: 'Revisione del sito {{url}}',
    /** Seconda riga. */
    intro: 'Nella schermata allegata i riquadri numerati indicano le parti da cambiare.',
    /** Titolo della nota generale. */
    general: 'Nota generale:',
    /** Nome del file allegato. */
    fileName: 'revisione',
  },
  /** App Android dei progetti (ADR 0062). */
  /** Ambienti del sito di un progetto (ADR 0050). */
  environments: {
    demo: 'Demo',
    prod: 'Produzione',
  },
  apps: {
    title: 'App Android',
    intro: 'Gli APK delle app dei progetti. Scaricali dal telefono Android e installali: vale l’ultima versione e si tengono le cinque più recenti.',
    listLabel: 'App Android dei progetti',
    loadFailed: 'Non è stato possibile leggere le app del set. Riprova.',
    download: 'Scarica APK',
    downloadOf: 'Scarica l’APK di {{name}}, versione {{version}}',
    rebuild: 'Nuova build',
    rebuildOf: 'Nuova build di {{name}}, {{variant}}',
    variants: {
      demo: 'Demo',
      production: 'Produzione',
    },
    talksToDemo: 'Parla con la demo.',
    talksToDemoUntil: 'Parla con la demo ed entra da sola fino al {{date}}.',
    talksToProduction: 'Parla con la produzione.',
    versionLine: 'Versione {{version}} · {{size}} · {{date}}',
    older: 'Versioni precedenti ({{count}})',
    installHelp: 'Sul telefono apri il file scaricato e, se richiesto, permetti l’installazione da questa fonte.',
    signing: {
      title: 'Certificato di firma',
      help: 'Le impronte servono solo ai servizi che le chiedono, come Firebase, Maps o App Links. Per l’accesso con Google non serve niente: l’app entra con l’accesso della piattaforma.',
      loading: 'Lettura del certificato…',
      missing: 'Il progetto non ha ancora una chiave di firma: nasce con la prima build.',
      sha1: 'Impronta SHA-1',
      sha256: 'Impronta SHA-256',
      failed: 'Non è stato possibile leggere il certificato. Riprova.',
    },
    failedHelp: 'L’ultima build non è riuscita. L’Institore la può correggere: chiediglielo in conversazione.',
    downloadFailed: 'Download non riuscito. Riprova.',
    startFailed: 'Non è stato possibile avviare la build. Riprova.',
    states: {
      queued: 'In coda',
      dispatched: 'In costruzione',
      signing: 'In firma',
      succeeded: 'Pronta',
      failed: 'Non riuscita',
      expired: 'Scaduta',
    },
    errors: {
      app_build_running: 'C’è già una build in corso per questo progetto.',
      demo_not_ready: 'La demo del progetto non è pronta.',
      production_not_ready: 'Il progetto non ha la produzione: serve per l’APK di produzione.',
      read_only_project: 'Il set ha il progetto in sola lettura.',
      app_build_dispatch_failed: 'GitHub non ha avviato la build. Riprova più tardi.',
      app_build_not_ready: 'Questa build non ha un APK da scaricare.',
      mobile_repository_missing: 'Il progetto non ha un repository GitHub collegato.',
      object_storage_disabled: 'L’archivio degli APK non è attivo.',
    },
  },
};

/** Forma dei testi degli artifacts. */
export type Artifacts = typeof artifacts;
